import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { makeStyles, MenuItem } from '@material-ui/core';
import { Autocomplete, TextField } from '@avangard/ui/core';

import { FormStyled } from '@modules/layout/styled';
import { useLazyLocations, useLazySchools, useRegions } from '@modules/shared/hooks';
import { useCurrentUser } from '@modules/user/hooks';
import { schoolTypeTranslates } from '@modules/schools/constants';
import { interestOptions, sportCategoryOptions } from '@modules/students/constants';

import type { RegionEntity, LocationEntity } from '@modules/shared/entities';
import type { StudentEntity } from '@modules/students/entities';
import type { SchoolEntity } from '@modules/schools/entities';
import type { UpdateStudentFormValues } from '@modules/students/requests';

type StudentEditFormProps = {
    student: StudentEntity;
};

const useTextAreaInputStyles = makeStyles({
    input: {
        minHeight: 56,
    },
});

const StudentEducationEditFormGroup = (_: StudentEditFormProps): React.ReactElement => {
    const { t } = useTranslation(['common', 'students']);

    const { values, errors, handleChange, handleBlur, setFieldValue } =
        useFormikContext<UpdateStudentFormValues>();

    const textAreaInputClasses = useTextAreaInputStyles();

    const { currentUser } = useCurrentUser();

    const { regions, loading: regionsLoading } = useRegions();
    const [getLocations, { locations, count: locationsCount, loading: locationsLoading }] =
        useLazyLocations();

    const [getSchools, { schools, count: schoolsCount, loading: schoolsLoading }] =
        useLazySchools();

    const getOptionLabel =
        (field: string, withTranslate?: boolean) =>
        (option: LocationEntity | SchoolEntity | AutocompleteOption | null): string => {
            if (!option?.[field]) {
                return '-';
            }

            if (withTranslate) {
                return t(option[field]);
            }

            return option[field];
        };

    const getOptionSelected =
        () =>
        (
            option: RegionEntity | LocationEntity | SchoolEntity | AutocompleteOption | null,
            value: RegionEntity | LocationEntity | SchoolEntity | AutocompleteOption | null,
        ): boolean => {
            if (!option || !value) {
                return false;
            }

            if ('key' in option && 'key' in value) {
                return option.key === value.key;
            }

            if ('id' in option && 'id' in value) {
                return option.id === value.id;
            }

            return false;
        };

    const getRegionOptionValue = (option?: string | null) => {
        const idx = regions.findIndex(region => region.id === option);

        if (idx > -1) {
            return regions[idx];
        }

        return null;
    };

    const getLocationOptionValue = (option?: string | null) => {
        const idx = locations.findIndex(location => location.id === option);

        if (idx > -1) {
            return locations[idx];
        }

        return null;
    };

    const getSchoolOptionValue = (option?: string | null) => {
        const idx = schools.findIndex(school => school.id === option);

        if (idx > -1) {
            return schools[idx];
        }

        return null;
    };

    const handleChangeSelect = (event: React.ChangeEvent<{ name: string; value: string }>) =>
        setFieldValue(event.target.name, event.target.value);

    const handleChangeAutocomplete =
        (id: string) => (event: React.ChangeEvent<{}>, value: any | null) => {
            setFieldValue(id, value?.id ?? '');

            if (id === 'education.schoolRegionId') {
                setFieldValue('education.schoolLocationId', '');
                setFieldValue('education.schoolId', '');
            }
        };

    // TODO: move in separate form group
    React.useEffect(() => {
        if (!values.education.schoolRegionId) {
            return;
        }

        getLocations({ variables: { regionId: values.education.schoolRegionId } });
    }, [values.education.schoolRegionId]);

    React.useEffect(() => {
        if (!values.education.schoolLocationId) {
            return;
        }

        getSchools({
            variables: {
                args: {
                    filter: {
                        location: {
                            id: values.education.schoolLocationId,
                        },
                    },
                    page: { all: true },
                },
            },
        });
    }, [values.education.schoolLocationId]);

    const chosenSchool = schools.find(school => school.id === values.education.schoolId);

    return (
        <>
            <FormStyled.FormColumn>
                <FormStyled.FormColumnTitle>
                    {t('students:form.titles.education_school')}
                </FormStyled.FormColumnTitle>

                <Autocomplete<RegionEntity | null, false, false, false>
                    required
                    id='education.schoolRegionId'
                    label={t('common:forms.labels.region') ?? ''}
                    placeholder={t('common:forms.placeholder.choose_region') ?? ''}
                    options={regions}
                    value={getRegionOptionValue(values.education.schoolRegionId)}
                    error={!!errors.education?.schoolRegionId}
                    helperText={errors.education?.schoolRegionId}
                    disabled={regionsLoading}
                    onChange={handleChangeAutocomplete('education.schoolRegionId')}
                    getOptionLabel={getOptionLabel('name')}
                    getOptionSelected={getOptionSelected()}
                />

                <Autocomplete<LocationEntity | null, false, false, false>
                    required
                    id='education.schoolLocationId'
                    label={t('common:forms.labels.location') ?? ''}
                    placeholder={t('common:forms.placeholder.choose_location') ?? ''}
                    options={locations}
                    value={getLocationOptionValue(values.education.schoolLocationId)}
                    error={!!errors.education?.schoolLocationId}
                    helperText={errors.education?.schoolLocationId}
                    disabled={
                        !values.education.schoolRegionId || locationsCount === 0 || locationsLoading
                    }
                    onChange={handleChangeAutocomplete('education.schoolLocationId')}
                    getOptionLabel={getOptionLabel('name')}
                    getOptionSelected={getOptionSelected()}
                />

                <Autocomplete<SchoolEntity | null, false, false, false>
                    required
                    id='schoolId'
                    label={t('common:forms.labels.school_short_name') ?? ''}
                    placeholder={t('common:forms.placeholder.choose_school_short_name') ?? ''}
                    options={schools}
                    value={getSchoolOptionValue(values.education.schoolId)}
                    error={!!errors.education?.schoolId}
                    helperText={errors.education?.schoolId}
                    disabled={
                        !values.education.schoolLocationId || schoolsCount === 0 || schoolsLoading
                    }
                    onChange={handleChangeAutocomplete('education.schoolId')}
                    getOptionLabel={getOptionLabel('shortName')}
                    getOptionSelected={getOptionSelected()}
                />

                <TextField
                    disabled
                    fullWidth
                    id='education.schoolType'
                    label={t('common:forms.labels.school_type') ?? ''}
                    placeholder={t('common:forms.placeholder.school_type') ?? ''}
                    value={chosenSchool?.type ? t(schoolTypeTranslates[chosenSchool.type]) : '-'}
                />

                <TextField
                    disabled
                    multiline
                    fullWidth
                    id='education.schoolFullName'
                    label={t('common:forms.labels.school_long_name')}
                    placeholder={t('common:forms.placeholder.school_long_name') ?? ''}
                    value={chosenSchool?.fullName}
                    InputProps={{
                        classes: textAreaInputClasses,
                    }}
                />

                <TextField
                    disabled
                    fullWidth
                    id='education.schoolNumber'
                    label={t('common:forms.labels.school_number')}
                    placeholder={t('common:forms.placeholder.school_number') ?? ''}
                    value={chosenSchool?.number}
                />

                <TextField
                    disabled
                    fullWidth
                    id='education.schoolClass'
                    label={t('students:form.labels.school_class')}
                    placeholder={t('students:form.placeholder.school_class') ?? ''}
                    value={chosenSchool?.profile}
                />

                <TextField
                    disabled={!currentUser.isSchool()}
                    fullWidth
                    id='education.schoolClassProfile'
                    label={t('students:form.labels.school_class_profile')}
                    placeholder={t('students:form.placeholder.school_class_profile') ?? ''}
                    value={values.education.schoolClassProfile}
                    error={!!errors.education?.schoolClassProfile}
                    helperText={errors.education?.schoolClassProfile}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
            </FormStyled.FormColumn>

            <FormStyled.FormColumn>
                <FormStyled.FormColumnTitle>
                    {t('students:form.titles.education_interest')}
                </FormStyled.FormColumnTitle>

                <Autocomplete<AutocompleteOption | null, true, true, false>
                    multiple
                    fullWidth
                    disableClearable
                    id='education.interest'
                    label={t('students:form.labels.interest') ?? ''}
                    placeholder={t('students:form.placeholder.interest') ?? ''}
                    options={interestOptions}
                    value={values.education.interest}
                    error={!!errors.education?.interest}
                    helperText={errors.education?.interest}
                    onChange={handleChangeAutocomplete('education.interest')}
                    getOptionSelected={getOptionSelected()}
                    getOptionLabel={getOptionLabel('name', true)}
                />

                <TextField
                    fullWidth
                    id='education.facultative'
                    label={t('students:form.labels.facultative')}
                    placeholder={t('students:form.placeholder.facultative') ?? ''}
                    value={values.education.facultative}
                    error={!!errors.education?.facultative}
                    helperText={errors.education?.facultative}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />

                <TextField
                    fullWidth
                    id='education.clubs'
                    label={t('students:form.labels.clubs')}
                    placeholder={t('students:form.placeholder.clubs') ?? ''}
                    value={values.education.clubs}
                    error={!!errors.education?.clubs}
                    helperText={errors.education?.clubs}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />

                <TextField
                    fullWidth
                    id='education.sportSection'
                    label={t('students:form.labels.sport_section')}
                    placeholder={t('students:form.placeholder.sport_section') ?? ''}
                    value={values.education.sportSection}
                    error={!!errors.education?.sportSection}
                    helperText={errors.education?.sportSection}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />

                <TextField
                    select
                    fullWidth
                    id='education.sportsCategory'
                    name='education.sportsCategory'
                    label={t('students:form.labels.sport_category')}
                    value={values.education.sportsCategory}
                    error={!!errors.education?.sportsCategory}
                    helperText={errors.education?.sportsCategory}
                    onChange={handleChangeSelect}
                    onBlur={handleBlur}
                >
                    <MenuItem value=''>
                        {t('students:form.placeholder.choose_sport_category')}
                    </MenuItem>

                    {sportCategoryOptions.map(sportCategory => (
                        <MenuItem key={sportCategory.key} value={sportCategory.key}>
                            {t(sportCategory.value)}
                        </MenuItem>
                    ))}
                </TextField>
            </FormStyled.FormColumn>
        </>
    );
};

export { StudentEducationEditFormGroup };
